// <date-range-picker> Web Component
// Wraps Litepicker for check-in / check-out date selection.
// Usage: <date-range-picker checkin="2026-03-20" checkout="2026-03-21"
//          placeholder="Check-in — Check-out"
//          checkin-name="checkin" checkout-name="checkout"></date-range-picker>

class DateRangePicker extends HTMLElement {
    connectedCallback() {
        this._picker = null;
        // _render writes our own innerHTML (hidden inputs + display) — no
        // parser race with SSR children (we don't have any) so this is safe
        // to call synchronously.
        this._render();
        // Defer _wire (Litepicker init) and the default-fill check to DCL.
        // Two reasons:
        //  1. Default-dates cache fix: data-default-days-ahead-* attrs let
        //     the picker compute today+N CLIENT-SIDE so the server doesn't
        //     bake stale dates into CDN-cached HTML.
        //     memory/default_dates_cache_fix_plan.md §3.1.
        //  2. main.js's loadSavedSearchParams() restores user's last-session
        //     dates from localStorage at DCL. We must run AFTER that so
        //     localStorage wins over defaults; the empty-check in
        //     _maybeFillDefaults preserves whichever ran first.
        // Litepicker init at _wire() captures startDate/endDate at init
        // time, so default-fill MUST precede Litepicker construction.
        const self = this;
        const init = () => {
            self._maybeFillDefaults();
            // PR-perf.4: Defer Litepicker init until the visitor actually
            // interacts with the field. Lightweight click/focus stub
            // triggers lazy-load on first use; until then we save ~16 KB
            // JS + 3 KB CSS + 447 ms scripting from cold load. The
            // already-fired interaction is replayed once Litepicker is
            // ready (synthesize open) so the user doesn't have to click
            // twice.
            self._wireFieldClickStub();
        };
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', init, { once: true });
        } else {
            init();
        }
    }

    _wireFieldClickStub() {
        const field = this.querySelector('.drp-field');
        if (!field) return;
        const self = this;
        const onFirstInteraction = function (e) {
            field.removeEventListener('click', onFirstInteraction);
            field.removeEventListener('focusin', onFirstInteraction);
            self._loadLitepicker(function () {
                self._wire();
                // Synthesize the original open effect — visitor's click
                // shouldn't need to be repeated.
                if (self._isMobile()) {
                    self._openOverlay();
                } else if (self._picker) {
                    self._picker.show();
                }
            });
        };
        field.addEventListener('click', onFirstInteraction);
        // Keyboard-only users: tabbing into the field should also load.
        field.addEventListener('focusin', onFirstInteraction);
    }

    // Idempotent + queueing — concurrent first-interactions on multiple
    // <date-range-picker> instances on the same page (home: 1, search: 1)
    // share the same load promise.
    _loadLitepicker(callback) {
        if (typeof Litepicker !== 'undefined') { callback && callback(); return; }
        if (DateRangePicker._loadCbs) {
            DateRangePicker._loadCbs.push(callback);
            return;
        }
        DateRangePicker._loadCbs = [callback];
        const css = document.createElement('link');
        css.rel = 'stylesheet';
        css.href = 'https://cdn.jsdelivr.net/npm/litepicker/dist/css/litepicker.css';
        document.head.appendChild(css);
        const script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/npm/litepicker/dist/litepicker.js';
        script.onload = function () {
            const cbs = DateRangePicker._loadCbs || [];
            DateRangePicker._loadCbs = null;
            for (let i = 0; i < cbs.length; i++) { try { cbs[i] && cbs[i](); } catch (e) {} }
        };
        document.head.appendChild(script);
    }

    // _maybeFillDefaults: if hidden inputs are empty (no URL, no localStorage)
    // AND the data-default-days-ahead-* attrs are valid integers, compute
    // today+N in UTC and set the hidden inputs + display. Does NOT dispatch
    // dates-change — that event represents user intent and writes
    // localStorage in main.js. Auto-defaults are initialization, not intent.
    _maybeFillDefaults() {
        const checkinInput = this.querySelector('.drp-checkin');
        const checkoutInput = this.querySelector('.drp-checkout');
        if (!checkinInput || !checkoutInput) return;
        // Already populated (URL or localStorage)? Don't overwrite.
        if (checkinInput.value || checkoutInput.value) return;
        const offsetIn = parseInt(this.getAttribute('data-default-days-ahead-checkin') || '0', 10);
        const offsetOut = parseInt(this.getAttribute('data-default-days-ahead-checkout') || '0', 10);
        if (!Number.isFinite(offsetIn) || !Number.isFinite(offsetOut)) return;
        if (offsetIn < 1 || offsetIn > 365) return;
        if (offsetOut <= offsetIn || offsetOut > 365) return;
        // UTC-truncated to start of day so date boundaries line up with the
        // server's UTC time.Now() semantics.
        const now = new Date();
        const todayUTCMs = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
        const inDate = new Date(todayUTCMs + offsetIn * 86400000);
        const outDate = new Date(todayUTCMs + offsetOut * 86400000);
        const fmt = (d) => d.toISOString().split('T')[0];
        checkinInput.value = fmt(inDate);
        checkoutInput.value = fmt(outDate);
        this._updateDisplay();
    }

    disconnectedCallback() {
        if (this._picker) this._picker.destroy();
    }

    get checkin() { return this.getAttribute('checkin') || ''; }
    set checkin(v) { this.setAttribute('checkin', v); this._updateDisplay(); }

    get checkout() { return this.getAttribute('checkout') || ''; }
    set checkout(v) { this.setAttribute('checkout', v); this._updateDisplay(); }

    _render() {
        const placeholder = this.getAttribute('placeholder') || 'Check-in — Check-out';
        const checkinName = this.getAttribute('checkin-name') || 'checkin';
        const checkoutName = this.getAttribute('checkout-name') || 'checkout';
        const checkin = this.checkin;
        const checkout = this.checkout;
        const hasDate = checkin && checkout;
        const displayText = hasDate ? this._formatRange(checkin, checkout) : placeholder;
        const textClass = hasDate ? 'text-gray-700' : 'text-gray-400';

        this.innerHTML = `
            <div class="drp-field flex-1 bg-white rounded flex items-center px-3 py-2.5 cursor-pointer">
                <svg class="w-5 h-5 text-gray-400 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                </svg>
                <span class="drp-display text-sm font-medium flex-1 ${textClass}">${displayText}</span>
                <input type="hidden" name="${checkinName}" value="${checkin}" class="drp-checkin">
                <input type="hidden" name="${checkoutName}" value="${checkout}" class="drp-checkout">
            </div>`;
    }

    _wire() {
        const field = this.querySelector('.drp-field');
        const checkinInput = this.querySelector('.drp-checkin');
        const checkoutInput = this.querySelector('.drp-checkout');

        if (typeof Litepicker === 'undefined') {
            console.warn('Litepicker not loaded');
            return;
        }

        // Create invisible input for Litepicker to attach to.
        // aria-label + tabIndex satisfy axe-core: presentational glue,
        // not user-focusable, but named for AT introspection.
        const dummy = document.createElement('input');
        dummy.type = 'text';
        dummy.style.cssText = 'position:absolute;opacity:0;pointer-events:none;width:0;height:0;';
        dummy.setAttribute('aria-label', 'Date range picker hidden input');
        dummy.tabIndex = -1;
        field.appendChild(dummy);

        const today = new Date();
        var isMobile = window.innerWidth < 768;
        this._picker = new Litepicker({
            element: dummy,
            singleMode: false,
            allowRepick: true,
            minDate: today,
            minDays: 1,
            numberOfMonths: isMobile ? 1 : 2,
            numberOfColumns: isMobile ? 1 : 2,
            format: 'YYYY-MM-DD',
            separator: ' — ',
            autoApply: true,
            startDate: checkinInput.value || null,
            endDate: checkoutInput.value || null,
            tooltipText: { one: 'night', other: 'nights' },
            tooltipNumber: (totalDays) => totalDays - 1,
            setup: (picker) => {
                picker.on('selected', (date1, date2) => {
                    if (date1 && date2) {
                        const ci = date1.format('YYYY-MM-DD');
                        const co = date2.format('YYYY-MM-DD');
                        checkinInput.value = ci;
                        checkoutInput.value = co;
                        this._updateDisplay();
                        this.dispatchEvent(new CustomEvent('dates-change', {
                            bubbles: true,
                            detail: { checkin: ci, checkout: co }
                        }));
                    }
                });
            }
        });

        field.addEventListener('click', () => {
            if (this._isMobile()) {
                this._openOverlay();
            } else if (this._picker) {
                this._picker.show();
            }
        });
    }

    _isMobile() {
        return window.matchMedia('(max-width: 767px)').matches;
    }

    _openOverlay() {
        if (this._overlay) return;

        const checkinInput = this.querySelector('.drp-checkin');
        const checkoutInput = this.querySelector('.drp-checkout');

        const overlay = document.createElement('div');
        overlay.className = 'drp-overlay fixed inset-0 z-[100] bg-white flex flex-col';
        overlay.innerHTML = `
            <div class="flex items-center gap-2 px-2 py-2 border-b border-gray-200 bg-white">
                <button type="button" class="drp-ov-close p-2 text-gray-700 flex-shrink-0" aria-label="Close">
                    <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
                    </svg>
                </button>
                <h2 class="flex-1 text-base font-semibold text-gray-900">Select dates</h2>
            </div>
            <div class="drp-ov-calendar flex-1 overflow-y-auto px-2 py-4">
                <style>.drp-ov-calendar .litepicker .container__footer{display:none!important}.drp-ov-calendar .litepicker{box-shadow:none;border:none;width:100%}.drp-ov-calendar .litepicker .container__months{width:100%}</style>
            </div>
            <div class="flex items-center justify-between gap-3 px-4 py-3 border-t border-gray-200 bg-white">
                <div class="drp-ov-summary text-sm text-gray-600 flex-1 truncate"></div>
                <button type="button" class="drp-ov-apply px-6 py-2 bg-booking-action text-white font-semibold rounded disabled:opacity-50 disabled:cursor-not-allowed" disabled>Apply</button>
            </div>
        `;
        document.body.appendChild(overlay);
        document.body.style.overflow = 'hidden';
        this._overlay = overlay;

        const calendar = overlay.querySelector('.drp-ov-calendar');
        const close = overlay.querySelector('.drp-ov-close');
        const apply = overlay.querySelector('.drp-ov-apply');
        const summary = overlay.querySelector('.drp-ov-summary');

        const anchor = document.createElement('div');
        calendar.appendChild(anchor);

        let pickedStart = null, pickedEnd = null;
        const today = new Date();

        const updateSummary = () => {
            if (pickedStart && pickedEnd) {
                summary.textContent = `${pickedStart.format('MMM D')} — ${pickedEnd.format('MMM D')}`;
                apply.disabled = false;
            } else if (pickedStart) {
                summary.textContent = `Check-in: ${pickedStart.format('MMM D')}`;
                apply.disabled = true;
            } else {
                summary.textContent = '';
                apply.disabled = true;
            }
        };

        this._ovPicker = new Litepicker({
            element: anchor,
            inlineMode: true,
            singleMode: false,
            allowRepick: true,
            minDate: today,
            minDays: 1,
            numberOfMonths: 2,
            numberOfColumns: 1,
            format: 'YYYY-MM-DD',
            separator: ' — ',
            autoApply: false,
            startDate: checkinInput.value || null,
            endDate: checkoutInput.value || null,
            tooltipText: { one: 'night', other: 'nights' },
            tooltipNumber: (totalDays) => totalDays - 1,
            setup: (picker) => {
                picker.on('preselect', (d1, d2) => {
                    pickedStart = d1 || null;
                    pickedEnd = d2 || null;
                    updateSummary();
                });
                picker.on('selected', (d1, d2) => {
                    pickedStart = d1;
                    pickedEnd = d2;
                    updateSummary();
                });
            }
        });

        if (checkinInput.value && checkoutInput.value) {
            pickedStart = this._ovPicker.getStartDate();
            pickedEnd = this._ovPicker.getEndDate();
            updateSummary();
        }

        close.addEventListener('click', () => this._closeOverlay());
        apply.addEventListener('click', () => {
            if (pickedStart && pickedEnd) {
                const ci = pickedStart.format('YYYY-MM-DD');
                const co = pickedEnd.format('YYYY-MM-DD');
                checkinInput.value = ci;
                checkoutInput.value = co;
                this._updateDisplay();
                this.dispatchEvent(new CustomEvent('dates-change', {
                    bubbles: true,
                    detail: { checkin: ci, checkout: co }
                }));
            }
            this._closeOverlay();
        });
    }

    _closeOverlay() {
        if (!this._overlay) return;
        if (this._ovPicker) { this._ovPicker.destroy(); this._ovPicker = null; }
        this._overlay.remove();
        this._overlay = null;
        document.body.style.overflow = '';
    }

    _updateDisplay() {
        const display = this.querySelector('.drp-display');
        const ci = this.querySelector('.drp-checkin');
        const co = this.querySelector('.drp-checkout');
        if (!display || !ci || !co) return;

        if (ci.value && co.value) {
            display.textContent = this._formatRange(ci.value, co.value);
            display.classList.remove('text-gray-400');
            display.classList.add('text-gray-700');
        }
    }

    _formatRange(checkin, checkout) {
        const opts = { month: 'short', day: 'numeric' };
        const ci = new Date(checkin);
        const co = new Date(checkout);
        return `${ci.toLocaleDateString('en-US', opts)} — ${co.toLocaleDateString('en-US', opts)}`;
    }
}

customElements.define('date-range-picker', DateRangePicker);
